import * as THREE from 'three';
import { RAIN } from '../utils/constants';

export type RainSystemHandle = {
  group: THREE.Group;
  update: (dt: number, cameraPos: THREE.Vector3) => void;
  setSplashCallback: (cb: (x: number, z: number) => void) => void;
};

const rainVertexShader = `
  attribute float size;
  attribute float velocity;
  varying float vAlpha;
  varying float vVelocity;
  
  void main() {
    vVelocity = velocity;
    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    gl_PointSize = size * (300.0 / -mvPosition.z);
    gl_Position = projectionMatrix * mvPosition;
    
    float dist = length(mvPosition.xyz);
    vAlpha = smoothstep(150.0, 20.0, dist) * ${RAIN.OPACITY.toFixed(2)};
  }
`;

const rainFragmentShader = `
  varying float vAlpha;
  varying float vVelocity;
  
  void main() {
    vec2 center = gl_PointCoord - vec2(0.5);
    float streak = abs(center.x) * 2.0;
    float alpha = (1.0 - streak * streak) * vAlpha;
    
    float lengthFade = smoothstep(0.0, 0.3, gl_PointCoord.y) * 
                       smoothstep(1.0, 0.7, gl_PointCoord.y);
    alpha *= lengthFade;
    
    vec3 color = vec3(0.7, 0.8, 0.95);
    gl_FragColor = vec4(color, alpha);
  }
`;

export function createRainSystem(): RainSystemHandle {
  const group = new THREE.Group();
  group.name = 'RainSystem';

  const count = RAIN.PARTICLE_COUNT;
  const positions = new Float32Array(count * 3);
  const velocities = new Float32Array(count);
  const sizes = new Float32Array(count);

  for (let i = 0; i < count; i++) {
    positions[i * 3] = (Math.random() - 0.5) * RAIN.SPREAD_XZ * 2;
    positions[i * 3 + 1] = Math.random() * RAIN.SPAWN_HEIGHT;
    positions[i * 3 + 2] = (Math.random() - 0.5) * RAIN.SPREAD_XZ * 2;

    velocities[i] = RAIN.FALL_SPEED * (0.8 + Math.random() * 0.4);
    sizes[i] = RAIN.STREAK_LENGTH * (0.6 + Math.random() * 0.8);
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('velocity', new THREE.BufferAttribute(velocities, 1));
  geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));

  const material = new THREE.ShaderMaterial({
    uniforms: {},
    vertexShader: rainVertexShader,
    fragmentShader: rainFragmentShader,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });

  const rain = new THREE.Points(geometry, material);
  rain.frustumCulled = false;
  group.add(rain);

  const splashGeometry = new THREE.BufferGeometry();
  const splashCount = 200;
  const splashPositions = new Float32Array(splashCount * 3);
  const splashAlphas = new Float32Array(splashCount);
  const splashSizes = new Float32Array(splashCount);
  const splashAges = new Float32Array(splashCount);

  for (let i = 0; i < splashCount; i++) {
    splashPositions[i * 3] = 0;
    splashPositions[i * 3 + 1] = -1000;
    splashPositions[i * 3 + 2] = 0;
    splashAlphas[i] = 0;
    splashSizes[i] = 0;
    splashAges[i] = 999;
  }

  splashGeometry.setAttribute('position', new THREE.BufferAttribute(splashPositions, 3));
  splashGeometry.setAttribute('alpha', new THREE.BufferAttribute(splashAlphas, 1));
  splashGeometry.setAttribute('size', new THREE.BufferAttribute(splashSizes, 1));

  const splashMaterial = new THREE.PointsMaterial({
    color: 0x8090a0,
    size: 0.8,
    transparent: true,
    opacity: 0.4,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    sizeAttenuation: true,
  });

  const splashes = new THREE.Points(splashGeometry, splashMaterial);
  splashes.frustumCulled = false;
  group.add(splashes);

  let splashCallback: ((x: number, z: number) => void) | null = null;
  let nextSplashIndex = 0;

  const spawnSplash = (x: number, z: number) => {
    const idx = nextSplashIndex;
    nextSplashIndex = (nextSplashIndex + 1) % splashCount;

    splashPositions[idx * 3] = x;
    splashPositions[idx * 3 + 1] = 0.05;
    splashPositions[idx * 3 + 2] = z;
    splashAges[idx] = 0;
    splashSizes[idx] = 0.3 + Math.random() * 0.5;
  };

  const update = (dt: number, cameraPos: THREE.Vector3) => {
    const posAttr = geometry.attributes.position;
    const velAttr = geometry.attributes.velocity;
    const pos = posAttr.array as Float32Array;
    const vel = velAttr.array as Float32Array;

    const windX = Math.sin(RAIN.ANGLE) * RAIN.FALL_SPEED;

    for (let i = 0; i < count; i++) {
      pos[i * 3] += windX * dt;
      pos[i * 3 + 1] -= vel[i] * dt;

      if (pos[i * 3 + 1] < 0) {
        const worldX = pos[i * 3] + cameraPos.x;
        const worldZ = pos[i * 3 + 2] + cameraPos.z;

        if (Math.random() < 0.1) {
          spawnSplash(worldX, worldZ);
          splashCallback?.(worldX, worldZ);
        }

        pos[i * 3] = (Math.random() - 0.5) * RAIN.SPREAD_XZ * 2;
        pos[i * 3 + 1] = RAIN.SPAWN_HEIGHT + Math.random() * 10;
        pos[i * 3 + 2] = (Math.random() - 0.5) * RAIN.SPREAD_XZ * 2;
      }

      const dx = pos[i * 3];
      const dz = pos[i * 3 + 2];
      if (Math.abs(dx) > RAIN.SPREAD_XZ || Math.abs(dz) > RAIN.SPREAD_XZ) {
        pos[i * 3] = (Math.random() - 0.5) * RAIN.SPREAD_XZ * 2;
        pos[i * 3 + 2] = (Math.random() - 0.5) * RAIN.SPREAD_XZ * 2;
      }
    }

    group.position.set(cameraPos.x, cameraPos.y, cameraPos.z);
    posAttr.needsUpdate = true;

    const splashPosAttr = splashGeometry.attributes.position;
    const splashPos = splashPosAttr.array as Float32Array;

    for (let i = 0; i < splashCount; i++) {
      if (splashAges[i] < 0.3) {
        splashAges[i] += dt;
        const t = splashAges[i] / 0.3;
        splashSizes[i] = (0.3 + Math.random() * 0.3) * (1 - t);
        splashAlphas[i] = 0.5 * (1 - t);

        const expand = t * 0.5;
        splashPos[i * 3] += (Math.random() - 0.5) * expand;
        splashPos[i * 3 + 2] += (Math.random() - 0.5) * expand;
      } else {
        splashPos[i * 3 + 1] = -1000;
      }
    }
    splashPosAttr.needsUpdate = true;
  };

  const setSplashCallback = (cb: (x: number, z: number) => void) => {
    splashCallback = cb;
  };

  return { group, update, setSplashCallback };
}
